
import type { Result } from '@empathyco/x-types'
import type { SetupContext } from 'vue'
import type { PropType } from 'vue/types/options'
import type { ExplainMessages } from '../../models/i18n/explain.messages'
import { EngagementActionsTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-actions-type.enum'
import { EngagementTypeComponentEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-type-component.enum'
import { defineComponent, getCurrentInstance, ref } from 'vue'
import { useAudit, useTagging, useXService } from '../../composables'
import { mapGetters } from '../../composables/utils'
import {
  AuditActionEnum,
  AuditTypeEnum,
  EngagementToolEnum,
  ExplainSectionEnum,
  FeatureIdEnum,
} from '../../enums'
import { AUTH_CONSTS } from '../../store/auth/auth.consts'
import { DownloadUtils } from '../../utils/download.utils'
import { ExplainCSVExportUtils } from '../../utils/explain-csv-export.utils'

import { UiTooltip } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'

export interface ExplainExportability {
  query: string
  messages: ExplainMessages['exportability']
  numberOfProductsSelected: number
  hasConfidentialData: boolean
  isBrowseContext: boolean
}

export default defineComponent({
  name: 'ExplainExportCsvButton',
  components: {
    EpbButton,
    UiTooltip,
  },
  props: {
    query: {
      type: String,
      required: true,
    },
    messages: {
      type: Object as PropType<ExplainMessages['exportability']>,
      default: undefined,
    },
    numberOfProductsSelected: {
      type: Number,
      required: true,
    },
    hasConfidentialData: {
      type: Boolean,
      required: true,
    },
    isBrowseContext: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: ExplainExportability, ctx: SetupContext) {
    const MAX_TOTAL_RESULTS = 1250
    const MAX_RESULTS_PER_CALL = 250

    const { auditAction } = useAudit()
    const { taggingAction } = useTagging()
    const xService = useXService()

    const {
      proxy: { $store, $epb },
    } = getCurrentInstance()
    const { tokenInfo } = mapGetters($store, AUTH_CONSTS.moduleName, {
      tokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })
    const loading = ref(false)

    const getResults = async (): Promise<Array<Result>> => {
      loading.value = true
      const arrResults: Array<Result> = []

      const fetchResults = async (rows: number, start: number): Promise<void> => {
        const { results } = await xService.search({ rows, start })
        arrResults.push(...results)
      }

      try {
        const numCalls = Math.ceil(
          Math.min(props.numberOfProductsSelected, MAX_TOTAL_RESULTS) / MAX_RESULTS_PER_CALL,
        )

        for (let i = 0; i < numCalls; i++) {
          const rows = props.numberOfProductsSelected / numCalls

          await fetchResults(rows, i * MAX_RESULTS_PER_CALL)
        }
        ctx.emit('download-succeed', true)
        await taggingAction({
          featureId: FeatureIdEnum.EXPLAIN,
          type: EngagementActionsTypeEnum.CLICK,
          component: EngagementTypeComponentEnum.DOWNLOAD,
        })
        await auditAction({
          action: EngagementTypeComponentEnum.DOWNLOAD,
          tool: EngagementToolEnum.EXPLAIN,
          params: {
            date: new Date().toISOString(),
            index: [props.query],
            payload: {
              action: AuditActionEnum.DOWNLOAD,
              objectType: AuditTypeEnum.EXPLAIN,
              nextState: {
                query: props.query,
                section: ExplainSectionEnum.SEARCH,
                number: props.numberOfProductsSelected,
              },
              objectId: crypto.randomUUID(),
            },
            user: String(tokenInfo.value.user.id).toUpperCase(),
          },
        })
      } catch (error) {
        ctx.emit('showError', $epb.utils.decodeErrorMessage(error))
        return
      } finally {
        loading.value = false
      }

      return arrResults
    }

    const onDownload = async (): Promise<void> => {
      const results = await getResults()
      if (results.length) {
        const currentDate = new Date()
          .toLocaleString(navigator.language, { hour12: false })
          .replace(/,/g, '')

        const filename = `Explain export ${currentDate.replace(/\//g, '-').replace(/:/g, '.')}`

        const csvColumns = {
          ...props.messages.csvColumns,
          queryText: props.isBrowseContext
            ? props.messages.csvColumns.queryTextBrowse
            : props.messages.csvColumns.queryText,
        }

        const fileContent = ExplainCSVExportUtils.generateCSV(
          props.query,
          String(tokenInfo.value.user.id).toUpperCase(),
          currentDate,
          results,
          csvColumns,
          $epb.locale,
          props.hasConfidentialData,
        )
        DownloadUtils.downloadCSV(fileContent, filename)
      }
    }

    return { onDownload, loading }
  },
})
